import React from "react";
import { Box, Typography, IconButton, Button, Divider } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PaymentIcon from "@mui/icons-material/Payment";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { useState } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import MeatballsMenu from "@Components/MeatballsMenu/MeatballsMenu";
import { DeletePopup } from "@Components/Popup/DeletePopup";

import { DocumentItemStyles } from "./GenericOrderInfoStyles";
import {
  inputTypes,
  Currency,
  paymentTypes,
  TransactionTypes,
  InvoiceStates,
} from "../../Utils/HelpingObjects/HelpingObjects";
import { EditSectionPopup } from "../GenericForm/EditSectionPopup";
import useSendPutRequest from "@Hooks/useSendPutRequest";
import usePostItem from "@Hooks/usePostItem";
import useDeleteItem from "../../Hooks/useDeleteItem";
import {
  UPDATE_INVOICES_URL,
  UPDATE_PAYMENTS_URL,
  ADD_PAYMENT_URL,
  DELETE_INVOICE_URL,
  DELETE_PAYMENT_URL,
} from "../../API/APIRoutes";
import axios from "axios";
import { useEffect } from "react";
import { options } from "joi";
import { AddPaymentPopup } from "./AddPaymentPopup";
import { add } from "date-fns";

export default function InvoiceList({
  invoices: initialInvoices,
  selectedObject,
  setSelectedObject,
  editable,
}) {
  const [openPayments, setOpenPayments] = useState({});
  //**************************** Invoice Edit ****************************/
  const [invoices, setInvoices] = useState(initialInvoices || []);
  const [showInvoiceEditPopup, setShowInvoiceEditPopup] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const { requestMethod: invoicePutItem } = useSendPutRequest();
  const [invoicePutItemLoading, setInvoicePutItemLoading] = useState(false);
  const { loading: insertPaymentLoading, postItem: insertPayment } =
    usePostItem();

  //**************************** Delete Invoice and Payment ****************************/
  const { loading: deleteItemLoading, deleteItem } = useDeleteItem();
  const [showDeleteInvoicePopup, setShowDeleteInvoicePopup] = useState(false);
  const [showDeletePaymentPopup, setShowDeletePaymentPopup] = useState(false);
  const [selectedInvoiceToDelete, setSelectedInvoiceToDelete] = useState(null);
  const [selectedPaymentToDelete, setSelectedPaymentToDelete] = useState(null);

  const openDeleteInvoicePopup = (invoice) => {
    setSelectedInvoiceToDelete(invoice);
    setShowDeleteInvoicePopup(true);
  };

  const closeDeleteInvoicePopup = () => {
    setShowDeleteInvoicePopup(false);
  };

  const confirmDeleteInvoice = async (invoiceId) => {
    await deleteItem(
      invoiceId,
      closeDeleteInvoicePopup,
      DELETE_INVOICE_URL,
      `Invoice Deleted Successfully!`,
      `Failed To Delete Invoice`
    );
    const updatedInvoices = invoices.filter(
      (invoice) => invoice.invoiceId !== invoiceId
    );
    setInvoices(updatedInvoices);
    window.location.reload();
    closeDeleteInvoicePopup();
  };

  const openDeletePaymentPopup = (payment) => {
    setSelectedPaymentToDelete(payment);
    setShowDeletePaymentPopup(true);
  };

  const closeDeletePaymentPopup = () => {
    setShowDeletePaymentPopup(false);
  };

  const confirmDeletePayment = async (paymentId, invoiceId) => {
    await deleteItem(
      paymentId,
      closeDeletePaymentPopup,
      DELETE_PAYMENT_URL,
      `Payment Deleted Successfully!`,
      `Failed To Delete Payment`
    );

    const updatedInvoices = invoices.map((invoice) => {
      if (invoice.invoiceId === invoiceId) {
        return {
          ...invoice,
          payments: invoice.payments.filter(
            (payment) => payment.paymentId !== paymentId
          ),
        };
      }
      return invoice;
    });
    setInvoices(updatedInvoices);
    window.location.reload();
    closeDeletePaymentPopup();
  };

  //**************************** MeatballsMenu Options ****************************/
  const invoiceMenuOptions = (invoice) => [
    {
      label: "Add Payment",
      icon: <PaymentIcon />,
      color: "primary",
      action: () => addNewPayment(invoice.invoiceId),
      showOption: editable,
    },
    {
      label: "Edit Invoice",
      icon: <EditIcon />,
      color: "primary",
      action: () => onEditInvoice(invoice),
      showOption: editable,
    },
    {
      label: "Delete Invoice",
      icon: <DeleteIcon />,
      color: "error",
      action: () => openDeleteInvoicePopup(invoice),
      showOption: editable,
    },
  ];

  const paymentMenuOptions = (payment) => [
    {
      label: "Edit Payment",
      icon: <EditIcon />,
      color: "primary",
      action: () => onEditPayment(payment),
      showOption: editable,
    },
    {
      label: "Delete Payment",
      icon: <DeleteIcon />,
      color: "error",
      action: () => openDeletePaymentPopup(payment),
      showOption: editable,
    },
  ];

  const invoiceFormFields = [
    {
      size: 12,
      type: inputTypes.TEXT,
      key: "transactionNumber",
      label: "Transaction Number",
      editable: false,
    },
    {
      size: 12,
      type: inputTypes.TEXT,
      key: "poNumber",
      label: "Po Number",
      editable: false,
    },
    {
      size: 12,
      type: inputTypes.TEXT,
      key: "coraId",
      label: "Cora Id",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "transactionType",
      label: "Transaction Type",
      options: TransactionTypes,
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.DATE,
      key: "releasedDate",
      label: "Released Date",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.DATE,
      key: "submissionDate",
      label: "Submission Date",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.DATE,
      key: "dueDate",
      label: "Due Date",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "state",
      label: "State",
      options: InvoiceStates,
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.FLOAT_NUMBER,
      key: "totalInvoiceCost",
      label: "Total Cost",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "currency",
      label: "Currency",
      options: Currency,
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.TEXT,
      key: "comment",
      label: "Comment",
      editable: true,
    },
  ];
  const formatInvoiceDate = (date) => {
    if (!date) return null;
    return typeof date.toDate === "function" ? date.toDate() : date;
  };

  const onEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceEditPopup(true);
  };
  const closeInvoiceEditPopup = () => setShowInvoiceEditPopup(false);

  const confirmInvoiceEdit = async (newInvoiceData, file) => {
    setInvoicePutItemLoading(true);
    // Create a copy of the data and remove the payments array
    const { payments, ...dataWithoutPayments } = newInvoiceData;

    const processedDataWithoutPayments = {
      ...dataWithoutPayments,
      releasedDate: formatInvoiceDate(dataWithoutPayments.releasedDate),
      submissionDate: formatInvoiceDate(dataWithoutPayments.submissionDate),
      dueDate: formatInvoiceDate(dataWithoutPayments.dueDate),
      isFileAttached: file ? true : false,
      isReal: true,
    };
    const processedData = {
      ...newInvoiceData,
      releasedDate: formatInvoiceDate(dataWithoutPayments.releasedDate),
      submissionDate: formatInvoiceDate(dataWithoutPayments.submissionDate),
      dueDate: formatInvoiceDate(dataWithoutPayments.dueDate),
      isFileAttached: file ? true : false,
      isReal: true,
    };
    const response = await invoicePutItem(
      UPDATE_INVOICES_URL,
      processedDataWithoutPayments,
      `Invoice Edited Successfully!`,
      `Failed To Edit Invoice`
    );
    // Extract preSignedUrl and objectKey outside the .then()
    if (file && response?.data?.preSignedUrl) {
      const { preSignedUrl, objectKey } = response.data;
      // Upload file to Minio
      await axios.put(preSignedUrl, file, {
        headers: {
          "Content-Type": "application/pdf",
        },
      });
    }
    // Update the local approvals array with the edited approval
    const updatedInvoices = invoices.map((invoice) => {
      if (
        invoice.invoiceId === newInvoiceData.invoiceId ||
        invoice.transactionNumber === newInvoiceData.transactionNumber
      ) {
        return processedData;
      }
      return invoice;
    });

    setInvoices(updatedInvoices);
    // Refresh the page to show the updated invoice
    window.location.href = window.location.href;
    closeInvoiceEditPopup();
  };
  //**************************** Payment Edit ****************************/
  const [showPaymentEditPopup, setShowPaymentEditPopup] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const { isLoading: paymentPutItemLoading, requestMethod: paymentPutItem } =
    useSendPutRequest();
  const paymentFormFields = [
    {
      size: 12,
      type: inputTypes.TEXT,
      key: "paymentName",
      label: "Payment Name",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "paymentType",
      label: "Payment Type",
      options: paymentTypes,
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.FLOAT_NUMBER,
      key: "amount",
      label: "Amount",
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.SEARCHABLE_DROPDOWN,
      key: "currency",
      label: "Currency",
      options: Currency,
      editable: true,
    },
    {
      size: 12,
      type: inputTypes.DATE,
      key: "date",
      label: "Payment Date",
      editable: true,
    },
  ];
  const onEditPayment = (payment) => {
    setSelectedPayment(payment);
    setShowPaymentEditPopup(true);
  };
  const closePaymentEditPopup = () => setShowPaymentEditPopup(false);

  const confirmPaymentEdit = async (newPaymentData) => {
    const processedData = {
      ...newPaymentData,
      date: formatInvoiceDate(newPaymentData.date),
    };
    await paymentPutItem(
      UPDATE_PAYMENTS_URL,
      processedData,
      `Payment Edited Successfully!`,
      `Failed To Edit Payment`
    );
    const updatedInvoices = invoices.map((invoice) => {
      if (invoice.invoiceId === newPaymentData.invoiceId) {
        return {
          ...invoice,
          payments: invoice.payments.map((payment) =>
            payment.paymentId === newPaymentData.paymentId
              ? processedData
              : payment
          ),
        };
      }
      return invoice;
    });

    setInvoices(updatedInvoices);
    window.location.reload();
    closePaymentEditPopup();
  };
  //**************************** Add Payment ****************************/
  const [showAddPaymentPopup, setShowAddPaymentPopup] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const closeAddPaymentPopup = () => setShowAddPaymentPopup(false);
  const confirmAddPayment = async (newPaymentData) => {
    const processedData = {
      ...newPaymentData,
      date: formatInvoiceDate(newPaymentData.date),
      paymentId: 10,
    };
    const response = await insertPayment(
      ADD_PAYMENT_URL,
      processedData,
      closeAddPaymentPopup,
      `Payment Added Successfully!`,
      `Failed To Add Payment`
    );
    const returnedResponse = response;
    const updatedInvoices = invoices.map((invoice) => {
      if (invoice.invoiceId === selectedInvoiceId) {
        return {
          ...invoice,
          payments: [...invoice.payments, returnedResponse],
        };
      }
      return invoice;
    });
    setInvoices(updatedInvoices);
    window.location.reload();
    closeAddPaymentPopup();
  };
  const addNewPayment = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setShowAddPaymentPopup(true);
  };
  //**************************** Invoice list ****************************/

  useEffect(() => {
    setInvoices(initialInvoices || []);
  }, [initialInvoices]);
  const toggleOpen = (invoiceId) => {
    setOpenPayments((prev) => ({
      ...prev,
      [invoiceId]: !prev[invoiceId],
    }));
  };
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <Box sx={{ maxHeight: "85vh", overflowY: "auto" }}>
      {invoices.map((invoice, idx) => {
        const invoiceId =
          invoice.objectKey || `${invoice.transactionNumber}-${idx}`;
        const isSelected = selectedObject?._invoicId === invoiceId;

        return (
          <Box
            key={invoiceId}
            onClick={() => {
              setSelectedObject({
                ...invoice,
                _invoicId: invoiceId,
              });
            }}
            sx={{
              ...DocumentItemStyles(isSelected),
              position: "relative", // Add position relative to container
            }}
          >
            {/* Meatballs menu for invoice actions */}
            {editable && (
              <Box
                sx={{ position: "absolute", right: 10, top: 10 }}
                onClick={(e) => e.stopPropagation()} // Stop propagation to prevent invoice selection
              >
                <MeatballsMenu options={invoiceMenuOptions(invoice)} />
              </Box>
            )}

            {/* Invoice Header - Transaction Type and Number */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  fontSize: "1.2rem",
                  color: "primary.main",
                  textAlign: "center",
                  width: "100%", // needed to fully center text
                }}
              >
                {invoice.transactionType}
              </Typography>

              <Box sx={{ width: "100%" }}>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  sx={{ color: "text.secondary", mt: 0.5 }}
                >
                  #{invoice.transactionNumber}
                </Typography>
              </Box>
            </Box>

            {/* State Chip - Positioned below header */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
              <Box
                sx={{
                  backgroundColor:
                    invoice.state === "APPROVED"
                      ? "success.light"
                      : invoice.state === "PENDING"
                      ? "warning.light"
                      : invoice.state === "REJECTED"
                      ? "error.light"
                      : "info.light",
                  color:
                    invoice.state === "APPROVED"
                      ? "success.dark"
                      : invoice.state === "PENDING"
                      ? "warning.dark"
                      : invoice.state === "REJECTED"
                      ? "error.dark"
                      : "info.dark",
                  px: 2,
                  py: 0.5,
                  borderRadius: 3,
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                {invoice.state || "N/A"}
              </Box>
            </Box>

            {/* Dates Section */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              {invoice.submissionDate && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    SUBMITTED
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDate(invoice.submissionDate)}
                  </Typography>
                </Box>
              )}

              {invoice.releasedDate && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    RELEASED
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDate(invoice.releasedDate)}
                  </Typography>
                </Box>
              )}

              {invoice.dueDate && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    DUE DATE
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDate(invoice.dueDate)}
                  </Typography>
                </Box>
              )}
            </Box>
            {/* Invoice Total Cost */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mb: 2,
              }}
            >
              {invoice.totalInvoiceCost && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Total Cost
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {invoice.totalInvoiceCost}
                  </Typography>
                </Box>
              )}

              {invoice.currency && (
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Currency
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {invoice.currency}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Comment Section */}
            {invoice.comment && (
              <Box
                sx={{
                  backgroundColor: "grey.50",
                  p: 1.5,
                  borderRadius: 1,
                  borderLeft: "4px solid",
                  borderLeftColor: "primary.main",
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                >
                  COMMENT
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {invoice.comment}
                </Typography>
              </Box>
            )}

            {/* Status and Payments Row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {invoice.releasedDate ? (
                  <CheckCircleIcon color="success" fontSize="small" />
                ) : (
                  <CancelIcon color="error" fontSize="small" />
                )}
                <Typography variant="body2" fontWeight="medium">
                  {invoice.releasedDate ? "Released" : "Not Released"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    backgroundColor: "primary.light",
                    color: "primary.dark",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <PaymentIcon fontSize="small" />
                  <Typography variant="body2" fontWeight="bold">
                    {invoice.payments.length}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleOpen(invoiceId);
                  }}
                  sx={{
                    backgroundColor: openPayments[invoiceId]
                      ? "success.light"
                      : "grey.100",
                    "&:hover": {
                      backgroundColor: openPayments[invoiceId]
                        ? "success.main"
                        : "grey.200",
                    },
                  }}
                >
                  {openPayments[invoiceId] ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ExpandLessIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </Box>

            {/* Payment details */}
            {openPayments[invoiceId] &&
              invoice.payments.map((payment, index) => (
                <Box
                  key={index}
                  sx={{
                    alignItems: "center",
                    mt: 4,
                    gap: 4,
                    mb: 7,
                    position: "relative",
                  }}
                >
                  <Divider sx={{ my: 2 }} />
                  {/* Meatballs menu for payment actions */}
                  {editable && (
                    <Box
                      sx={{ position: "absolute", right: 10, top: 10 }}
                      onClick={(e) => e.stopPropagation()} // Stop propagation to prevent bubbling up
                    >
                      <MeatballsMenu options={paymentMenuOptions(payment)} />
                    </Box>
                  )}
                  <Box display="flex" alignItems="center" mb={2}>
                    <PaymentIcon sx={{ ml: 1, mr: 2 }} />
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ fontSize: "1rem" }}
                    >
                      {payment.paymentName}
                    </Typography>
                  </Box>
                  <Box mb={2} ml={3} display="flex" gap={4}>
                    <Box minWidth={250}>
                      <Typography variant="subtitle1">
                        Payment Amount: {payment.amount}
                      </Typography>
                    </Box>
                    <Box minWidth={250}>
                      <Typography variant="subtitle1">
                        Payment Currency: {payment.currency}
                      </Typography>
                    </Box>
                  </Box>

                  <Box mb={2} ml={3} display="flex" gap={4}>
                    <Box minWidth={250}>
                      <Typography variant="subtitle1">
                        Payment Type: {payment.paymentType.replace(/_/g, " ")}
                      </Typography>
                    </Box>
                    <Box minWidth={250}>
                      <Typography variant="subtitle1">
                        Payment Date: {formatDate(payment.date)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            <hr />
          </Box>
        );
      })}
      {EditSectionPopup(
        "Invoice",
        showInvoiceEditPopup,
        closeInvoiceEditPopup,
        confirmInvoiceEdit,
        invoicePutItemLoading,
        invoiceFormFields,
        selectedInvoice,
        true
      )}
      {EditSectionPopup(
        "Payment",
        showPaymentEditPopup,
        closePaymentEditPopup,
        confirmPaymentEdit,
        paymentPutItemLoading,
        paymentFormFields,
        selectedPayment
      )}
      {AddPaymentPopup(
        selectedInvoiceId,
        showAddPaymentPopup,
        closeAddPaymentPopup,
        confirmAddPayment,
        insertPaymentLoading
      )}
      {/* Delete Popups */}
      {DeletePopup(
        "Invoice",
        showDeleteInvoicePopup,
        closeDeleteInvoicePopup,
        () => confirmDeleteInvoice(selectedInvoiceToDelete?.invoiceId),
        null,
        deleteItemLoading
      )}
      {DeletePopup(
        "Payment",
        showDeletePaymentPopup,
        closeDeletePaymentPopup,
        () =>
          confirmDeletePayment(
            selectedPaymentToDelete?.paymentId,
            selectedPaymentToDelete?.invoiceId
          ),
        null,
        deleteItemLoading
      )}
    </Box>
  );
}
